import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'scatter',
  name_en: 'Scatter',
  name_zh: '散点图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    size: amount => amount > 1,
    color: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y: { itemType: [DEFINE.NUMBER], category: DEFINE.SINGLE },
    size: { itemType: [DEFINE.NUMBER], category: DEFINE.SINGLE, optional: true },
    color: { itemType: [DEFINE.STR], category: DEFINE.SINGLE, optional: true }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    size?: string;
    color?: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { layout, data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, size, color }
    } = spec;
    const data = dataArray[dataIndex];
    const presetOption = hook.buildStandardChartPresetOption(api);

    // * -------------------------- data
    function findMinMax(arr: number[]) {
      return arr.reduce(
        (acc, curr) => ({
          min: Math.min(acc.min, curr),
          max: Math.max(acc.max, curr)
        }),
        { min: Infinity, max: -Infinity }
      );
    }
    function customMap(
      value: number,
      inputMin: number,
      inputMax: number,
      outputMin: number,
      outputMax: number
    ) {
      // 这里使用了一个自定义的非线性映射函数，你可以根据需要自行修改
      return (
        outputMin + ((value - inputMin) / (inputMax - inputMin)) ** 2 * (outputMax - outputMin)
      );
    }

    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const sizeRangeMap = {
      [util.const.LAYOUT.SMALL]: [8, 28],
      [util.const.LAYOUT.SMALL_2_1]: [8, 46],
      [util.const.LAYOUT.SMALL_1_2]: [8, 46],
      [util.const.LAYOUT.LARGE]: [16, 92],
      [util.const.LAYOUT.LARGE_3_2]: [20, 120]
    };
    const sizeRange = sizeRangeMap[layout];
    const colorList = presetOption?.color || [
      '#c23531',
      '#2f4554',
      '#61a0a8',
      '#d48265',
      '#91c7ae',
      '#749f83',
      '#ca8622',
      '#bda29a',
      '#6e7074',
      '#546570',
      '#c4ccd3',
      '#f05b72',
      '#ef5b9c',
      '#f47920',
      '#905a3d',
      '#fab27b',
      '#2a5caa',
      '#444693',
      '#726930',
      '#b2d235'
    ];
    const { sizeData = [], colorData = [] } = util.dataEncoding.encoding(data.values, {
      sizeData: {
        field: size!,
        transforms: [{ type: 'toNumeric' }]
      },
      colorData: {
        field: color!,
        transforms: [{ type: 'deDuplication' }]
      }
    });
    const { min, max } = findMinMax(sizeData as number[]);
    const colorMap = {} as { [k: string]: string };
    colorData.forEach((item, index) => {
      colorMap[item] = colorList[index % colorList.length];
    });

    const xAxisData = [];

    const seriesData = data.values.map(item => {
      xAxisData.push(+item[x]);
      return {
        value: [+item[x], +item[y]],
        name: typeof color !== 'undefined' ? item[color] : '',
        itemStyle: typeof color !== 'undefined' ? { color: colorMap[item[color]] } : {},
        symbolSize:
          typeof size !== 'undefined'
            ? customMap(+item[size], min, max, sizeRange[0], sizeRange[1])
            : undefined
      };
    });

    // * -------------------------- chart
    const xName = x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : '');
    const series = [
      {
        type: 'scatter',
        xAxisIndex: 0,
        yAxisIndex: 0,
        __encodeField: y,
        name: y,
        data: seriesData,
        symbolSize: (sizeRange[0] + sizeRange[1]) / 3
      }
    ];

    return {
      legend: {
        data: []
      },
      tooltip: {
        formatter: params_ => {
          const { color: colorTemp, seriesType, name, value, seriesName } = params_;
          if (seriesType === 'scatter') {
            const resultX = hook.formatDataNumberValue(value[0], {
              type: 'table',
              unit: dataMetaInfo[x]?.unit
            });
            const resultY = hook.formatDataNumberValue(value[1], {
              type: 'table',
              unit: dataMetaInfo[y]?.unit
            });
            const items = [
              {
                type: util.const.SERIES_2_LEGEND_TYPE_MAP[
                  seriesType as keyof (typeof util)['const']['SERIES_2_LEGEND_TYPE_MAP']
                ],
                color: colorTemp,
                name: '',
                value: ''
              },
              {
                name: x,
                value: `${resultX.value + resultX.unit}`
              },
              {
                name: y,
                value: `${resultY.value + resultY.unit}`
              }
            ];

            return hook.buildTooltip({
              config: hook.buildStandardChartPresetOption(api).tooltip,
              title: name || '',
              items
            });
          }
        }
      },
      xAxis: {
        show: true,
        __encodeField: x,
        type: 'value',
        name: xName,
        dvNameLocation: undefined,
        // x轴上的data是为了洞察获取数据
        data: xAxisData,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        show: true,
        __encodeField: [y],
        type: 'value',
        name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      series
    };
  }
});
